import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import FloatingButtons from '../../components/feature/FloatingButtons';
import AgendamentoModal from '../../components/feature/AgendamentoModal';
import AnimatedSection from '../../components/base/AnimatedSection';
import PageEntrance from '../../components/base/PageEntrance';
import type { FormStatus } from '../../types';

// ─── Formulário de Contato ────────────────────────────────────────────────────

interface FormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const INITIAL_FORM: FormData = { name: '', email: '', phone: '', subject: '', message: '' };

function ContatoForm() {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setFormStatus('sending');
    try {
      const body = new URLSearchParams(formData as unknown as Record<string, string>);
      const res = await fetch('https://readdy.ai/api/form/d6os76l39lnhn4hh1gfg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setFormStatus(res.ok ? 'success' : 'error');
      if (res.ok) setFormData(INITIAL_FORM);
    } catch {
      setFormStatus('error');
    }
  };

  if (formStatus === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16 text-center">
        <div className="w-16 h-16 bg-[#C4A57B]/15 rounded-full flex items-center justify-center mb-4">
          <i className="ri-check-line text-[#C4A57B] text-3xl" />
        </div>
        <h4 className="text-xl font-medium text-[#4A3F35] mb-2">Mensagem Enviada!</h4>
        <p className="text-sm text-[#8B7355] mb-6">Obrigada pelo contato. Retornaremos em breve.</p>
        <button
          onClick={() => setFormStatus('idle')}
          className="px-6 py-2.5 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer"
        >
          Enviar Nova Mensagem
        </button>
      </div>
    );
  }

  return (
    <form
      id="contato-pacientes"
      data-readdy-form
      onSubmit={handleSubmit}
      className="space-y-4 sm:space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-[#8B7355] mb-1.5">Nome completo *</label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Seu nome"
            className="w-full px-4 py-3 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#4A3F35] placeholder-[#C4B49A] focus:outline-none focus:border-[#C4A57B] transition-colors"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#8B7355] mb-1.5">Telefone / WhatsApp</label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(48) 99999-9999"
            className="w-full px-4 py-3 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#4A3F35] placeholder-[#C4B49A] focus:outline-none focus:border-[#C4A57B] transition-colors"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-[#8B7355] mb-1.5">E-mail *</label>
        <input
          type="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          placeholder="seu@email.com"
          className="w-full px-4 py-3 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#4A3F35] placeholder-[#C4B49A] focus:outline-none focus:border-[#C4A57B] transition-colors"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-[#8B7355] mb-1.5">Assunto</label>
        <select
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          className="w-full px-4 py-3 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#4A3F35] focus:outline-none focus:border-[#C4A57B] transition-colors cursor-pointer"
        >
          <option value="">Selecione um assunto</option>
          <option value="Agendamento de Consulta">Agendamento de Consulta</option>
          <option value="Dúvida sobre Tratamentos">Dúvida sobre Tratamentos</option>
          <option value="HIFU — Ultrassom Microfocado">HIFU — Ultrassom Microfocado</option>
          <option value="Tricologia">Tricologia</option>
          <option value="Pequenos Procedimentos">Pequenos Procedimentos</option>
          <option value="Dermatologia Clínica">Dermatologia Clínica</option>
          <option value="Outro">Outro</option>
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-[#8B7355] mb-1.5">
          Mensagem *{' '}
          <span className={`ml-1 ${formData.message.length > 500 ? 'text-red-500' : 'text-[#A89580]'}`}>
            {formData.message.length}/500
          </span>
        </label>
        <textarea
          name="message"
          required
          value={formData.message}
          onChange={handleChange}
          maxLength={500}
          rows={4}
          placeholder="Descreva sua dúvida ou o que deseja tratar..."
          className="w-full px-4 py-3 bg-white border border-[#E8DCC8] rounded-lg text-sm text-[#4A3F35] placeholder-[#C4B49A] focus:outline-none focus:border-[#C4A57B] transition-colors resize-none"
        />
      </div>

      {formStatus === 'error' && (
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <i className="ri-error-warning-line text-red-500 text-base" />
          <p className="text-xs text-red-600">
            Ocorreu um erro. Tente novamente ou entre em contato pelo WhatsApp.
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={formStatus === 'sending' || formData.message.length > 500}
        className="w-full py-3.5 bg-[#C4A57B] text-white text-sm font-medium rounded-lg hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >
        {formStatus === 'sending' ? (
          <>
            <i className="ri-loader-4-line animate-spin text-base" />
            Enviando...
          </>
        ) : (
          <>
            <i className="ri-send-plane-line text-base" />
            Enviar Mensagem
          </>
        )}
      </button>
    </form>
  );
}

// ─── Seção Hero ───────────────────────────────────────────────────────────────

function HeroSection({ onAgendar }: { onAgendar: () => void }) {
  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="relative flex items-center pt-16 min-h-screen">
      {/* Imagem de fundo */}
      <div className="absolute inset-0 overflow-hidden">
        <img
          src="https://storage.readdy-site.link/project_files/4709dab1-64f1-4c72-8d6d-4bafa74c3cd9/cec3cca8-9069-47cb-8543-2e3938579357_LAURA-37-Direita.png?v=f39730ecef74bee33f99bea5bdc869bf"
          alt="Dra. Laura Folchini dermatologista em Criciúma e Tubarão SC atendimento humanizado e especializado"
          className="w-full h-full object-cover"
          style={{ objectPosition: '70% top' }}
        />
        {/* Overlay gradiente */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#1C130B]/75 via-[#2C1810]/45 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent" />

        {/* Marca d'água — mobile: só símbolo LF */}
        <div className="absolute inset-0 flex sm:hidden items-center justify-center pointer-events-none">
          <img
            src="https://storage.readdy-site.link/project_files/4709dab1-64f1-4c72-8d6d-4bafa74c3cd9/1edc2c63-9fc8-4568-9058-b66e9748acb4_Laura-Logo-1.png?v=129c3767f9cc60bb934342775df75f95"
            alt=""
            aria-hidden="true"
            className="select-none"
            style={{
              width: '380px',
              opacity: 0.06,
              filter: 'brightness(0) invert(1)',
              mixBlendMode: 'screen',
              clipPath: 'inset(0 0 32% 0)',
            }}
          />
        </div>

        {/* Marca d'água — desktop: logo completa */}
        <div className="absolute inset-0 hidden sm:flex items-center justify-start pointer-events-none">
          <img
            src="https://storage.readdy-site.link/project_files/4709dab1-64f1-4c72-8d6d-4bafa74c3cd9/1edc2c63-9fc8-4568-9058-b66e9748acb4_Laura-Logo-1.png?v=129c3767f9cc60bb934342775df75f95"
            alt=""
            aria-hidden="true"
            className="w-[500px] lg:w-[620px] xl:w-[720px] h-auto object-contain sm:ml-10 lg:ml-20 select-none"
            style={{
              opacity: 0.07,
              filter: 'brightness(0) invert(1)',
              mixBlendMode: 'screen',
            }}
          />
        </div>
      </div>

      <div className="relative z-10 w-full max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 py-16 sm:py-32 2xl:px-16">
        {/* Conteúdo alinhado à esquerda */}
        <div className="max-w-2xl 2xl:max-w-3xl">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full mb-8 opacity-0 translate-y-4"
            style={{ animation: 'fadeInUp 0.6s ease-out 0.1s forwards' }}
          >
            <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
            <span className="text-sm text-white/90 font-medium tracking-wide">CRM/SC 21.308 · RQE 26.072</span>
          </div>

          <h2
            className="text-4xl sm:text-5xl md:text-6xl 2xl:text-7xl font-light text-white mb-6 leading-[1.05] opacity-0 translate-y-4"
            style={{ animation: 'fadeInUp 0.7s ease-out 0.2s forwards' }}
          >
            Sua pele merece<br />
            o melhor<br />
            <span className="italic text-[#E8CFA8]">cuidado.</span>
          </h2>

          <p
            className="text-base sm:text-lg text-white/70 mb-10 tracking-wide opacity-0 translate-y-4"
            style={{ animation: 'fadeInUp 0.7s ease-out 0.32s forwards' }}
          >
            Criciúma e Tubarão — SC
          </p>

          <div
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-12 sm:mb-20 opacity-0 translate-y-4"
            style={{ animation: 'fadeInUp 0.7s ease-out 0.45s forwards' }}
          >
            <button
              onClick={onAgendar}
              className="px-8 py-4 bg-white text-[#8B7355] text-base font-medium rounded-full hover:bg-[#FBF8F3] transition-colors whitespace-nowrap cursor-pointer"
            >
              Agendar consulta
            </button>
            <button
              onClick={() => scrollTo('tratamentos')}
              className="px-8 py-4 bg-transparent text-white text-base font-medium rounded-full border-2 border-white/70 hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              Conhecer tratamentos
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Seção Sobre ─────────────────────────────────────────────────────────────

function SobreSection({ onAgendar }: { onAgendar: () => void }) {
  const credentials = [
    { icon: 'ri-shield-check-line', title: 'CRM/SC 21.308 · RQE 26.072', sub: 'Registro Profissional Ativo' },
    { icon: 'ri-award-line', title: 'Membro Titular da SBD', sub: 'Sociedade Brasileira de Dermatologia' },
    { icon: 'ri-graduation-cap-line', title: 'UNESC · Policlínica Geral — RJ', sub: 'Graduação e Especialização' },
    { icon: 'ri-map-pin-2-line', title: 'Criciúma & Tubarão — SC', sub: 'Atendimento em duas localidades' },
  ];

  return (
    <section id="sobre" className="py-16 sm:py-28 bg-white">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">

          {/* ─ Left: Text ─ */}
          <AnimatedSection direction="left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FBF8F3] rounded-full mb-8">
              <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
              <span className="text-sm text-[#8B7355] font-medium">Sobre a Dra.</span>
            </div>

            <h2 className="text-4xl sm:text-5xl md:text-6xl 2xl:text-7xl font-light text-[#4A3F35] leading-[1.06] mb-8">
              Dra. Laura<br />
              <span className="italic">Folchini</span>
            </h2>

            {/* Blockquote */}
            <blockquote className="border-l-2 border-[#C4A57B] pl-5 mb-8">
              <p className="text-base sm:text-lg text-[#8B7355] italic font-light leading-relaxed">
                "Cada paciente é único. Meu compromisso é um cuidado personalizado que respeita sua individualidade e promova o seu bem-estar."
              </p>
            </blockquote>

            <p className="text-sm sm:text-base text-[#8B7355] mb-4 leading-relaxed">
              Médica graduada pela Universidade do Extremo Sul Catarinense (UNESC), com especialização médica em Dermatologia pela Policlínica Geral do Rio de Janeiro. Membro titular da Sociedade Brasileira de Dermatologia.
            </p>
            <p className="text-sm sm:text-base text-[#8B7355] mb-8 leading-relaxed">
              Atua na dermatologia clínica, cirúrgica e estética, aliando conhecimento técnico a um atendimento humanizado e personalizado com foco no bem-estar do paciente.
            </p>

            {/* Timeline de formação */}
            <div className="mb-10 space-y-0">
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#C4A57B] mt-1 flex-shrink-0" />
                  <div className="w-px flex-1 bg-[#E8DCC8] mt-1" style={{ minHeight: '32px' }} />
                </div>
                <div className="pb-6">
                  <p className="text-xs font-semibold text-[#C4A57B] tracking-widest uppercase mb-0.5">2014</p>
                  <p className="text-sm font-medium text-[#4A3F35]">Graduação em Medicina</p>
                  <p className="text-xs text-[#A89580] mt-0.5">UNESC — Universidade do Extremo Sul Catarinense</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#C4A57B] mt-1 flex-shrink-0" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#C4A57B] tracking-widest uppercase mb-0.5">2024</p>
                  <p className="text-sm font-medium text-[#4A3F35]">Especialização em Dermatologia</p>
                  <p className="text-xs text-[#A89580] mt-0.5">Policlínica Geral do Rio de Janeiro</p>
                </div>
              </div>
            </div>

            {/* Stats — 3 items */}
            <div className="flex gap-6 sm:gap-8 mb-10 pb-10 border-b border-[#E8DCC8]">
              {[
                { value: '12+', label: 'Anos de experiência' },
                { value: '2', label: 'Clínicas em SC' },
                { value: '3', label: 'Áreas de atuação' },
              ].map(({ value, label }, i) => (
                <div key={label} className={i > 0 ? 'pl-6 sm:pl-8 border-l border-[#E8DCC8]' : ''}>
                  <p className="text-3xl sm:text-4xl font-light text-[#C4A57B] leading-none mb-1.5">{value}</p>
                  <p className="text-xs text-[#A89580] whitespace-nowrap">{label}</p>
                </div>
              ))}
            </div>

            {/* Credentials — 2-column card grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {credentials.map(({ icon, title, sub }) => (
                <div key={title} className="flex items-start gap-3 p-4 bg-[#FBF8F3] rounded-2xl border border-[#E8DCC8]">
                  <div className="w-9 h-9 bg-white rounded-full flex items-center justify-center flex-shrink-0 border border-[#E8DCC8]">
                    <i className={`${icon} text-[#C4A57B] text-base`} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#4A3F35] leading-snug mb-0.5">{title}</p>
                    <p className="text-[11px] text-[#A89580] leading-snug mt-0.5">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </AnimatedSection>

          {/* ─ Right: Photos ─ */}
          <AnimatedSection direction="right" className="lg:pt-10">
            <div className="relative">

              {/* Main large photo */}
              <div className="rounded-3xl overflow-hidden h-[460px] sm:h-[580px] w-full">
                <img
                  src="https://static.readdy.ai/image/99ef0297d19566c41bf6e48f2ac82c61/9f2d9cc6a68d45a3da75969b5da3b564.jpeg"
                  alt="Dra. Laura Folchini dermatologista consultório Criciúma SC tratamentos especializados"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Small accent photo — top right */}
              <div className="absolute -top-5 -right-3 sm:-right-5 w-28 sm:w-36 h-36 sm:h-44 rounded-2xl overflow-hidden border-4 border-white">
                <img
                  src="https://static.readdy.ai/image/99ef0297d19566c41bf6e48f2ac82c61/bba3cad1cb48d32b88b858d48f1c4884.jpeg"
                  alt="Dra. Laura Folchini KASA DERMA Criciúma SC consulta dermatológica"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Small accent photo — mid right */}
              <div className="absolute top-1/2 -translate-y-1/2 -right-3 sm:-right-5 w-24 sm:w-32 h-28 sm:h-36 rounded-2xl overflow-hidden border-4 border-white">
                <img
                  src="https://static.readdy.ai/image/99ef0297d19566c41bf6e48f2ac82c61/04cb998315324ec30a8e5f0149b66526.jpeg"
                  alt="Dra. Laura Folchini procedimentos dermatológicos Criciúma SC"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// ─── Seção Tratamentos (preview) ─────────────────────────────────────────────

const TREATMENT_CARDS = [
  {
    type: 'list' as const,
    icon: 'ri-scissors-cut-line',
    title: 'Pequenos procedimentos',
    desc: 'Realizados com técnica precisa e mínima cicatriz, em ambiente ambulatorial seguro e confortável.',
    items: ['Remoção de lesões benignas', 'Biópsia de pele para diagnóstico', 'Criocirurgia', 'Eletrocoagulação'],
    link: '/tratamentos#pequenos-procedimentos',
    delay: 0,
  },
  {
    type: 'image' as const,
    img: 'https://public.readdy.ai/ai/img_res/3640b1ed93d20c186ac11326ec0045bd.jpg',
    alt: 'Equipamento HIFU ultrassom microfocado tecnologia estética dermatológica Criciúma SC',
    title: 'Tecnologias e medicina estética',
    desc: 'Equipamentos modernos e técnicas atualizadas para os melhores resultados com máxima segurança e naturalidade.',
    delay: 100,
  },
  {
    type: 'list' as const,
    icon: 'ri-magic-line',
    title: 'Tecnologias e medicina estética',
    desc: 'Procedimentos estéticos com tecnologia de ponta para rejuvenescimento, firmeza e harmonia facial.',
    items: ['Toxina botulínica', 'Bioestimulador de colágeno', 'Luz intensa pulsada', 'Laser ablativo', 'Radiofrequência monopolar', 'Ultrassom macro e microfocado', 'MMP facial e capilar', 'Peelings'],
    link: '/tratamentos#tecnologias-estetica',
    delay: 200,
  },
  {
    type: 'image' as const,
    img: 'https://readdy.ai/api/search-image?query=elegant%20woman%20with%20flawless%20glowing%20skin%20receiving%20gentle%20facial%20skincare%20treatment%20in%20a%20luxurious%20warm-toned%20dermatology%20clinic%2C%20soft%20beige%20and%20warm%20cream%20tones%2C%20natural%20light%2C%20serene%20and%20feminine%20atmosphere%2C%20professional%20aesthetic%20medicine%20setting%2C%20close-up%20beauty%20portrait%20with%20healthy%20radiant%20skin&width=600&height=400&seq=derm-clinica-fem-02&orientation=landscape',
    alt: 'Tratamento de dermatologia clínica pele saudável Dra. Laura Folchini Criciúma Tubarão SC',
    title: 'Dermatologia clínica',
    desc: 'Diagnóstico e tratamento completo das principais condições dermatológicas com abordagem humanizada.',
    delay: 300,
  },
  {
    type: 'list' as const,
    icon: 'ri-heart-pulse-line',
    title: 'Dermatologia clínica',
    desc: 'Diagnóstico e tratamento das principais doenças da pele com acompanhamento personalizado.',
    items: ['Acne', 'Rosácea', 'Psoríase', 'Dermatite atópica', 'Melasma', 'Alopecia androgenética', 'Dermatite seborreica', 'Hiperidrose', 'Hidradenite supurativa', 'Micoses', 'Vitiligo'],
    link: '/tratamentos#dermatologia-clinica',
    delay: 400,
  },
];

function TratamentosSection({ onAgendar }: { onAgendar: () => void }) {
  return (
    <section id="tratamentos" className="py-16 sm:py-24 bg-[#FBF8F3] border-t border-[#E8DCC8]">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <AnimatedSection className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 sm:gap-8 mb-10 sm:mb-16">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6">
              <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
              <span className="text-sm text-[#8B7355] font-medium">Tratamentos</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] leading-tight">
              Tratamentos
            </h2>
            <p className="text-base sm:text-lg text-[#8B7355] mt-4 max-w-xl leading-relaxed">
              Cada tratamento é cuidadosamente indicado após avaliação individual, garantindo segurança, eficácia e resultados que respeitam a naturalidade de cada paciente.
            </p>
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 gap-5 sm:gap-6 2xl:gap-8">
          {TREATMENT_CARDS.map((card, i) => (
            <AnimatedSection key={i} delay={card.delay}>
              {card.type === 'image' ? (
                <div className="bg-white rounded-2xl overflow-hidden border border-[#E8DCC8] h-full">
                  <div className="h-44 sm:h-48">
                    <img src={card.img} alt={(card as { alt?: string }).alt ?? card.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <div className="p-5 sm:p-6">
                    <h3 className="text-lg font-medium text-[#4A3F35] mb-2">{card.title}</h3>
                    <p className="text-sm text-[#8B7355] leading-relaxed">{card.desc}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E8DCC8] hover:shadow-lg transition-shadow h-full">
                  <div className="w-12 h-12 bg-[#FBF8F3] rounded-full flex items-center justify-center mb-6">
                    <i className={`${card.icon} text-[#C4A57B] text-2xl`} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-medium text-[#4A3F35] mb-4">{card.title}</h3>
                  <p className="text-[#8B7355] leading-relaxed mb-4 text-sm sm:text-base">{card.desc}</p>
                  <ul className="space-y-1 mb-6">
                    {card.items?.map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-[#8B7355]">
                        <i className="ri-check-line text-[#C4A57B]" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={card.link!}
                    className="inline-flex items-center gap-2 text-sm text-[#C4A57B] hover:text-[#B39568] transition-colors cursor-pointer font-medium"
                  >
                    Saiba mais
                    <i className="ri-arrow-right-line text-base" />
                  </Link>
                </div>
              )}
            </AnimatedSection>
          ))}

          {/* CTA Card */}
          <AnimatedSection delay={500}>
            <div className="bg-[#C4A57B] rounded-2xl p-6 sm:p-8 text-white h-full flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-calendar-check-line text-white text-2xl" />
                </div>
                <h4 className="text-xl font-medium mb-3">Agendar Consulta</h4>
                <p className="text-white/80 text-sm leading-relaxed mb-6">
                  Agende sua consulta e receba um atendimento personalizado, humanizado e focado no seu bem-estar.
                </p>
              </div>
              <button
                onClick={onAgendar}
                className="inline-block px-6 py-3 bg-white text-[#C4A57B] text-sm font-medium rounded-full hover:bg-[#FBF8F3] transition-colors whitespace-nowrap cursor-pointer"
              >
                Agendar pelo WhatsApp
              </button>
            </div>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={600} className="text-center mt-12 sm:mt-16">
          <Link
            to="/tratamentos"
            className="inline-flex items-center gap-3 px-10 py-4 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer shadow-md"
          >
            Ver Todos os Tratamentos
            <i className="ri-arrow-right-line text-lg" />
          </Link>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── Seção Depoimentos ────────────────────────────────────────────────────────

const TESTIMONIALS = [
  { name: 'A. P.', city: 'Criciúma, SC', treatment: 'HIFU — Ultrassom Microfocado', initials: 'AP', color: 'bg-[#C4A57B]', text: 'Fiz o HIFU com a Dra. Laura e o resultado foi incrível! Minha pele ficou muito mais firme e o contorno do rosto melhorou bastante. O atendimento foi super atencioso, ela explicou tudo detalhadamente antes do procedimento. Recomendo demais!' },
  { name: 'F. S.', city: 'Criciúma, SC', treatment: 'Dermatologia Clínica — Acne', initials: 'FS', color: 'bg-[#8B7355]', text: 'Sofri com acne por anos e depois de consultar com a Dra. Laura minha pele transformou completamente. Ela foi muito cuidadosa no diagnóstico e o tratamento foi personalizado para o meu caso. Estou muito feliz com os resultados!' },
  { name: 'M. C.', city: 'Criciúma, SC', treatment: 'Dermatologia Clínica — Melasma', initials: 'MC', color: 'bg-[#A89580]', text: 'Tratei melasma com a Dra. Laura e os resultados superaram minhas expectativas. Ela é extremamente competente e cuidadosa. Me senti muito segura durante todo o procedimento. Além disso, é uma pessoa incrível, muito acolhedora e paciente.' },
  { name: 'J. P.', city: 'Tubarão, SC', treatment: 'Pequenos Procedimentos', initials: 'JP', color: 'bg-[#C4A57B]', text: 'Removi uma lesão com a Dra. Laura e fiquei impressionada com a delicadeza e precisão do procedimento. Praticamente não senti dor, a cicatriz ficou mínima e o acompanhamento pós-procedimento foi impecável. Muito obrigada!' },
  { name: 'C. M.', city: 'Laguna, SC', treatment: 'Tecnologias e Medicina Estética', initials: 'CM', color: 'bg-[#8B7355]', text: 'Fiz bioestimulador de colágeno e o resultado foi maravilhoso! A Dra. Laura é extremamente competente e cuidadosa. Me senti muito segura durante todo o procedimento. Já indiquei para várias amigas e todas adoraram!' },
  { name: 'P. L.', city: 'Içara, SC', treatment: 'HIFU — Lifting Facial', initials: 'PL', color: 'bg-[#A89580]', text: 'Tinha muito receio de fazer procedimentos estéticos, mas a Dra. Laura me deixou completamente à vontade. O HIFU foi tranquilo, sem dor, e o resultado foi natural e bonito. Já indiquei para várias amigas e todas adoraram!' },
];

function DepoimentosSection({ onAgendar }: { onAgendar: () => void }) {
  return (
    <section id="depoimentos" className="py-16 sm:py-24 bg-white border-t border-[#E8DCC8]">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <AnimatedSection className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FBF8F3] rounded-full mb-6">
            <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
            <span className="text-sm text-[#8B7355] font-medium">Depoimentos</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] leading-tight mb-4">
            O que dizem nossos pacientes
          </h2>
          <p className="text-base sm:text-lg text-[#8B7355] max-w-2xl mx-auto">
            A satisfação de cada paciente é o maior reconhecimento do nosso trabalho.
          </p>
        </AnimatedSection>

        <AnimatedSection delay={100}>
          <div className="flex gap-8 justify-center mb-10 sm:mb-16 max-w-xs mx-auto">
            {[
              { value: '98%', label: 'de satisfação' },
              { value: '5★', label: 'avaliação média' },
            ].map(({ value, label }, i) => (
              <div key={label} className={`text-center ${i === 1 ? 'pl-8 border-l border-[#E8DCC8]' : ''}`}>
                <p className="text-3xl sm:text-4xl font-light text-[#C4A57B] mb-1">{value}</p>
                <p className="text-xs text-[#8B7355]">{label}</p>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-3 gap-5 sm:gap-6 2xl:gap-8">
          {TESTIMONIALS.map((item, i) => (
            <AnimatedSection key={item.name} delay={i * 80}>
              <div className="bg-[#FBF8F3] rounded-2xl p-6 sm:p-8 border border-[#E8DCC8] hover:shadow-lg transition-shadow duration-300 h-full flex flex-col">
                <div className="flex items-center gap-1 mb-4">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <i key={s} className="ri-star-fill text-[#C4A57B] text-base" />
                  ))}
                </div>
                <div className="mb-6 flex-1">
                  <i className="ri-double-quotes-l text-[#E8DCC8] text-4xl leading-none block mb-2" />
                  <p className="text-[#6B5345] leading-relaxed text-sm">{item.text}</p>
                </div>
                <div className="mb-5">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white rounded-full text-xs text-[#8B7355] border border-[#E8DCC8]">
                    <i className="ri-leaf-line text-[#C4A57B]" />
                    {item.treatment}
                  </span>
                </div>
                <div className="flex items-center gap-3 pt-5 border-t border-[#E8DCC8]">
                  <div className={`w-10 h-10 ${item.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                    <span className="text-white text-xs font-semibold">{item.initials}</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#4A3F35]">{item.name}</p>
                    <p className="text-xs text-[#A89580]">{item.city}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={200} className="text-center mt-10 sm:mt-14">
          <p className="text-[#8B7355] mb-6 text-base">Pronto para transformar a sua pele também?</p>
          <button
            onClick={onAgendar}
            className="inline-block px-10 py-4 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer"
          >
            Agendar Minha Consulta
          </button>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── Seção Atendimento ────────────────────────────────────────────────────────

const CLINICS = [
  {
    city: 'Criciúma — SC',
    name: 'KASA DERMA',
    desc: 'Clínica especializada em dermatologia estética e clínica, com estrutura moderna, equipamentos de última geração e equipe dedicada ao seu bem-estar. Ambiente acolhedor e atendimento personalizado.',
    address: 'Criciúma, SC',
    hours: 'Seg a Sex — horários mediante agendamento',
    linkIcon: 'ri-instagram-line',
    linkLabel: 'Saiba Mais',
    linkHref: 'https://www.instagram.com/kasaderma/',
    direction: 'left' as const,
  },
  {
    city: 'Tubarão — SC',
    name: 'Grupo Naiana SA',
    desc: 'Centro médico de referência regional com infraestrutura completa para atendimento dermatológico de alta qualidade. Equipe multidisciplinar e tecnologia avançada a serviço da sua saúde.',
    address: 'Tubarão, SC',
    hours: 'Seg a Sex — horários mediante agendamento',
    linkIcon: 'ri-global-line',
    linkLabel: 'www.naianasa.com.br',
    linkHref: 'https://www.naianasa.com.br',
    direction: 'right' as const,
  },
];

function AtendimentoSection() {
  return (
    <section id="atendimento" className="py-16 sm:py-24 bg-[#FBF8F3]">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <AnimatedSection className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6">
            <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
            <span className="text-sm text-[#8B7355] font-medium">Atendimento</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] leading-tight mb-4">
            Locais de atendimento
          </h2>
          <p className="text-base sm:text-lg text-[#8B7355] max-w-2xl mx-auto">
            Atendo em duas clínicas de referência em Santa Catarina, com estrutura completa e ambiente acolhedor para o seu cuidado.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {CLINICS.map((clinic) => (
            <AnimatedSection key={clinic.name} direction={clinic.direction}>
              <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E8DCC8] hover:shadow-lg transition-shadow h-full">
                <div className="flex items-start justify-between mb-6">
                  <p className="text-sm text-[#8B7355] font-medium">{clinic.city}</p>
                  <i className="ri-arrow-right-up-line text-[#C4A57B] text-xl" />
                </div>
                <h3 className="text-xl sm:text-2xl font-medium text-[#4A3F35] mb-3">{clinic.name}</h3>
                <p className="text-[#8B7355] mb-6 leading-relaxed text-sm sm:text-base">{clinic.desc}</p>
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-map-pin-line text-[#C4A57B]" />
                    <span className="text-sm text-[#8B7355]">{clinic.address}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className="ri-time-line text-[#C4A57B]" />
                    <span className="text-sm text-[#8B7355]">{clinic.hours}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <i className={`${clinic.linkIcon} text-[#C4A57B]`} />
                    <a
                      href={clinic.linkHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-[#8B7355] hover:text-[#C4A57B] transition-colors cursor-pointer"
                    >
                      {clinic.linkLabel}
                    </a>
                  </div>
                </div>
                <a
                  href={clinic.linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-6 py-2.5 bg-transparent text-[#C4A57B] text-sm font-medium rounded-full border-2 border-[#C4A57B] hover:bg-[#FBF8F3] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Saiba Mais
                </a>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Seção Localização ────────────────────────────────────────────────────────

const MAP_LOCATIONS = [
  {
    city: 'Criciúma',
    clinic: 'KASA DERMA',
    src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55234.89537800842!2d-49.42!3d-28.68!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDQwJzQ4LjAiUyA0OcKwMjUnMTIuMCJX!5e0!3m2!1spt-BR!2sbr!4v1234567890',
    direction: 'left' as const,
  },
  {
    city: 'Tubarão',
    clinic: 'Grupo Naiana SA',
    src: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d55234.89537800842!2d-49.01!3d-28.47!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDI4JzEyLjAiUyA0OcKwMDAnMzYuMCJX!5e0!3m2!1spt-BR!2sbr!4v1234567890',
    direction: 'right' as const,
  },
];

function LocalizacaoSection() {
  return (
    <section id="localizacao" className="py-16 sm:py-24 bg-white">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <AnimatedSection className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FBF8F3] rounded-full mb-6">
            <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
            <span className="text-sm text-[#8B7355] font-medium">Localização</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] leading-tight mb-4">
            Como chegar
          </h2>
          <p className="text-base sm:text-lg text-[#8B7355] max-w-2xl mx-auto">
            Atendimento em duas localidades estratégicas em Santa Catarina
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {MAP_LOCATIONS.map(({ city, clinic, src, direction }) => (
            <AnimatedSection key={city} direction={direction}>
              <div className="bg-white rounded-2xl overflow-hidden border border-[#E8DCC8] h-full">
                <div className="p-6 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-medium text-[#4A3F35] mb-2">{city}</h3>
                  <p className="text-[#8B7355] mb-6">{clinic}</p>
                  <div className="aspect-video rounded-xl overflow-hidden">
                    <iframe
                      src={src}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Mapa ${city}`}
                    />
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Seção Contato ────────────────────────────────────────────────────────────

const CONTACT_ITEMS = [
  { icon: 'ri-whatsapp-line', label: 'WhatsApp', value: '(48) 99100-2443', href: 'https://wa.me/5548991002443' },
  { icon: 'ri-mail-line', label: 'E-mail', value: 'contato@dralaurafolchini.com.br', href: 'mailto:contato@dralaurafolchini.com.br' },
  { icon: 'ri-instagram-line', label: 'Instagram', value: '@laurafolchini', href: 'https://www.instagram.com/laurafolchini/' },
];

function ContatoSection({ onAgendar }: { onAgendar: () => void }) {
  return (
    <section id="contato" className="py-16 sm:py-24 bg-[#FBF8F3]">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <AnimatedSection direction="left">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full mb-6">
              <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
              <span className="text-sm text-[#8B7355] font-medium">Contato</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] mb-6 leading-tight">
              Entre em contato
            </h2>
            <p className="text-base sm:text-lg text-[#8B7355] mb-8 leading-relaxed">
              Estou à disposição para esclarecer suas dúvidas e agendar sua consulta.
            </p>
            <div className="space-y-5 sm:space-y-6 mb-8">
              {CONTACT_ITEMS.map(({ icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#FBF8F3] rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${icon} text-[#C4A57B] text-xl`} />
                  </div>
                  <div>
                    <p className="text-sm text-[#8B7355] font-medium mb-1">{label}</p>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#4A3F35] hover:text-[#C4A57B] transition-colors cursor-pointer break-all"
                    >
                      {value}
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={onAgendar}
              className="inline-block px-8 py-3 bg-[#C4A57B] text-white text-sm font-medium rounded-full hover:bg-[#B39568] transition-colors whitespace-nowrap cursor-pointer"
            >
              Agendar pelo WhatsApp
            </button>
          </AnimatedSection>

          <AnimatedSection direction="right">
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E8DCC8]">
              <h3 className="text-xl sm:text-2xl font-medium text-[#4A3F35] mb-2">Envie uma mensagem</h3>
              <p className="text-sm text-[#8B7355] mb-6 sm:mb-8">Respondemos em até 24 horas úteis.</p>
              <ContatoForm />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// ─── Seção FAQ ────────────────────────────────────────────────────────────────

const FAQS = [
  { question: 'Qual o horário de atendimento?', answer: 'De segunda-feira a sexta-feira, das 08:00 às 12:00 e das 13:30 às 18:00.' },
  { question: 'Como posso agendar uma consulta?', answer: 'O agendamento é feito pelo WhatsApp (48) 99100-2443. Basta entrar em contato para verificar disponibilidade de horários em Criciúma/SC e Tubarão/SC.' },
  { question: 'Quais são as formas de pagamento?', answer: 'Aceitamos Transferência TED/PIX, Dinheiro e Cartão de Crédito e Débito. Não aceitamos Cheque.' },
  { question: 'Como devo me preparar para a consulta dermatológica?', answer: 'Recomendamos vir sem maquiagem, base ou protetor solar com cor, para permitir uma avaliação mais precisa da pele. Hidratante e protetor solar sem cor podem ser usados normalmente.' },
];

function FaqSection({ onAgendar }: { onAgendar: () => void }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIndex((prev) => (prev === i ? null : i));

  return (
    <section className="py-16 sm:py-24 bg-white">
      <div className="max-w-4xl 2xl:max-w-5xl mx-auto px-4 sm:px-8 2xl:px-16">
        <AnimatedSection className="text-center mb-10 sm:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FBF8F3] rounded-full mb-6">
            <div className="w-2 h-2 bg-[#C4A57B] rounded-full" />
            <span className="text-sm text-[#8B7355] font-medium">FAQ</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl 2xl:text-6xl font-light text-[#4A3F35] leading-tight">
            Perguntas frequentes
          </h2>
        </AnimatedSection>

        <div className="space-y-3 sm:space-y-4">
          {FAQS.map((faq, i) => (
            <AnimatedSection key={i} delay={i * 80}>
              <div className="bg-[#FBF8F3] border border-[#E8DCC8] rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
                <button
                  onClick={() => toggle(i)}
                  className="w-full px-5 sm:px-8 py-5 sm:py-6 flex items-center justify-between text-left cursor-pointer"
                >
                  <span className="text-sm sm:text-base font-medium text-[#4A3F35] pr-4">{faq.question}</span>
                  <i className={`ri-arrow-down-s-line text-[#C4A57B] text-2xl flex-shrink-0 transition-transform duration-300 ${openIndex === i ? 'rotate-180' : ''}`} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openIndex === i ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <div className="px-5 sm:px-8 pb-5 sm:pb-6">
                    <p className="text-sm text-[#8B7355] leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={200}>
          <div className="mt-12 sm:mt-16 bg-[#FBF8F3] border border-[#E8DCC8] rounded-2xl p-8 sm:p-10 text-center">
            <div className="w-14 h-14 bg-[#C4A57B]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-question-answer-line text-[#C4A57B] text-2xl" />
            </div>
            <h3 className="text-xl font-medium mb-3">Ainda tem dúvidas?</h3>
            <p className="text-[#8B7355] text-sm leading-relaxed mb-6 max-w-md mx-auto">
              Agende sua consulta e tire todas as suas dúvidas diretamente com a Dra. Laura.
            </p>
            <button
              onClick={onAgendar}
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#25D366] text-white text-sm font-medium rounded-full hover:bg-[#1ebe5d] transition-colors whitespace-nowrap cursor-pointer shadow-md"
            >
              <i className="ri-whatsapp-line text-lg" />
              Agendar consulta pelo WhatsApp
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── Seção CTA Banner ─────────────────────────────────────────────────────────

function CtaBannerSection({ onAgendar }: { onAgendar: () => void }) {
  return (
    <section className="py-16 sm:py-24 bg-[#8B7355]">
      <div className="max-w-7xl 2xl:max-w-screen-2xl mx-auto px-4 sm:px-8 2xl:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          <AnimatedSection direction="left">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 sm:p-12 border border-white/20">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-6">
                <div className="w-2 h-2 bg-white rounded-full" />
                <span className="text-sm text-white font-medium">Cuide da Sua Pele</span>
              </div>
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-light text-white mb-4 leading-tight">
                Sua pele saudável começa com uma consulta especializada
              </h3>
              <p className="text-white/80 text-sm leading-relaxed mb-8">
                Seja para tratar uma condição específica, realizar um procedimento estético ou simplesmente fazer uma avaliação preventiva — estou aqui para cuidar de você com atenção e dedicação.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <button
                  onClick={onAgendar}
                  className="px-8 py-3 bg-white text-[#8B7355] text-sm font-medium rounded-full hover:bg-[#FBF8F3] transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Agendar Consulta
                </button>
                <button
                  onClick={() => document.getElementById('tratamentos')?.scrollIntoView({ behavior: 'smooth' })}
                  className="px-8 py-3 bg-transparent text-white text-sm font-medium rounded-full border-2 border-white hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer text-center"
                >
                  Ver Tratamentos
                </button>
              </div>
            </div>
          </AnimatedSection>
          <AnimatedSection direction="right">
            {/* Imagem relacionada a dermatologia / cuidado com a pele */}
            <div className="rounded-2xl overflow-hidden h-64 sm:h-96 border border-white/20 relative">
              <img
                src="https://readdy.ai/api/search-image?query=elegant%20woman%20bare%20shoulder%20and%20upper%20back%20with%20smooth%20flawless%20glowing%20skin%2C%20warm%20amber%20golden%20side%20light%20casting%20soft%20shadows%20on%20skin%20curves%2C%20luxury%20skincare%20beauty%20editorial%20photography%2C%20no%20face%20visible%20shot%20from%20behind%20or%20side%20cropped%20above%20shoulder%2C%20creamy%20beige%20ivory%20warm%20toned%20background%2C%20high%20end%20dermatology%20beauty%20campaign%2C%20radiant%20healthy%20skin%20texture%20detail%2C%20silky%20smooth%20skin%20surface%2C%20professional%20studio%20warm%20light%2C%20premium%20aesthetic%20medicine&width=800&height=700&seq=shoulder-back-skin-warmgold-v37&orientation=portrait"
                alt="Pele saudável e radiante resultado de tratamento dermatológico especializado Criciúma SC"
                loading="lazy"
                className="w-full h-full object-cover object-center"
              />
              {/* Overlay degradê bege (#C6A27A) com transparência */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#C6A27A]/50 via-[#C6A27A]/15 to-transparent" />
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export default function HomePage() {
  const [agendamentoOpen, setAgendamentoOpen] = useState(false);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const timer = setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const openAgendar = () => setAgendamentoOpen(true);
  const closeAgendar = () => setAgendamentoOpen(false);

  return (
    <>
      <title>Dermatologista Criciúma e Tubarão SC | Dra. Laura Folchini</title>
      <meta name="description" content="Dra. Laura Folchini, dermatologista CRM/SC 21.308. Dermatologia clínica, estética e cirúrgica em Criciúma e Tubarão — SC. Agende sua consulta pelo WhatsApp." />
      <link rel="canonical" href="https://dralaurafolchini.com.br/" />
      <div className="min-h-screen bg-[#FBF8F3]">
        <PageEntrance />
        {agendamentoOpen && <AgendamentoModal onClose={closeAgendar} />}
        <Navbar onAgendar={openAgendar} />
        <HeroSection onAgendar={openAgendar} />
        <SobreSection onAgendar={openAgendar} />
        <TratamentosSection onAgendar={openAgendar} />
        <DepoimentosSection onAgendar={openAgendar} />
        <AtendimentoSection />
        <LocalizacaoSection />
        <ContatoSection onAgendar={openAgendar} />
        <FaqSection onAgendar={openAgendar} />
        <CtaBannerSection onAgendar={openAgendar} />
        <Footer />
        <FloatingButtons onAgendar={openAgendar} />
      </div>
    </>
  );
}
